import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import React from 'react';
import { useWorkspacePanel } from './hooks/use-workspace-panel';
import { ResizeEnable } from 'react-rnd';
import { minimumDimensionsByPanelType } from './panel-configuration';
import { WorkspacePanel } from './workspace-panel';

export type ExpandablePanelType = 'EVENT' | 'BOLO' | 'MEDIA_ATTACHMENTS';
export type ExpandablePanelSize = 'XL' | 'XXL' | 'NORMAL';

export const expandablePanelSizeConfig: Record<ExpandablePanelSize, number> = {
    XL: 1000,
    XXL: 1500,
    NORMAL: 500,
};

type SetExpandablePanelSize = React.Dispatch<React.SetStateAction<ExpandablePanelSize>>;

const ExpandablePanelSizeContext = React.createContext<ExpandablePanelSize>('NORMAL');
const SetExpandablePanelSizeContext = React.createContext<undefined | SetExpandablePanelSize>(
    undefined
);

const useInitialPanelSize = <
    WorkspacePanelType extends ExpandablePanelType
>(): ExpandablePanelSize => {
    const workspacePanel = useWorkspacePanel<WorkspacePanelType>();

    // When a panel opens, it will only have a `temporaryId` if it was newly
    // opened during the session.
    // If the panel is being restored from our previous session, it will not have a `temporaryId`
    const isNewPanel = !!workspacePanel.temporaryId;
    const { eventPanelCompactByDefault } = useEnabledFeatures();

    // If we are opening a new panel, obey our preference for default size
    if (isNewPanel) {
        return eventPanelCompactByDefault ? 'NORMAL' : 'XL';
    }
    // If we are restoring an existing panel, attempt to make a best guess
    // at which size it was
    //
    // Currently, we can only restore to NORMAL, or XL.  Without adding
    // more metadata to the panel, I don't think its trivial to be able to
    // restore an XXL panel to its XXL state -- it will just be XL
    else {
        if (workspacePanel.width <= expandablePanelSizeConfig.NORMAL) {
            return 'NORMAL';
        } else {
            return 'XL';
        }
    }
};

export function ExandablePanelSizeProvider(props: {
    children: React.ReactElement;
}): React.ReactElement {
    const initialPanelSize = useInitialPanelSize();
    const [expandablePanelSize, setExpandablePanelSize] = React.useState<ExpandablePanelSize>(
        initialPanelSize
    );

    return (
        <ExpandablePanelSizeContext.Provider value={expandablePanelSize}>
            <SetExpandablePanelSizeContext.Provider value={setExpandablePanelSize}>
                {props.children}
            </SetExpandablePanelSizeContext.Provider>
        </ExpandablePanelSizeContext.Provider>
    );
}

export function useExpandablePanelSize(): ExpandablePanelSize {
    return React.useContext(ExpandablePanelSizeContext);
}

export function useSetExpandablePanelSize(): SetExpandablePanelSize {
    const val = React.useContext(SetExpandablePanelSizeContext);
    if (!val) throw new Error('Unexpectedly did not find event panel size setter on context');
    return val;
}

export function useExpandablePanelSizeState(panel: WorkspacePanel, type: ExpandablePanelType) {
    const expandablePanelSize = useExpandablePanelSize();

    const resizeOptions: ResizeEnable = React.useMemo(() => {
        if (expandablePanelSize === 'NORMAL') {
            return {
                top: true,
                bottom: true,
                left: false,
                right: false,
            };
        } else {
            return {
                top: true,
                bottom: true,
                left: true,
                right: true,
                topRight: true,
                bottomRight: true,
                bottomLeft: true,
                topLeft: true,
            };
        }
    }, [expandablePanelSize]);

    const sizeProps = React.useMemo(() => {
        const minExpandablePanelSize = expandablePanelSizeConfig[expandablePanelSize];
        if (expandablePanelSize === 'NORMAL') {
            return {
                minWidth: minExpandablePanelSize,
                minHeight: minimumDimensionsByPanelType[type]![1],
                maxWidth: minExpandablePanelSize,
                maxHeight: 9999,
            };
        } else if (expandablePanelSize === 'XL') {
            return {
                minWidth: minExpandablePanelSize,
                minHeight: minimumDimensionsByPanelType[type]![1],
            };
        } else {
            return {
                minWidth: minExpandablePanelSize,
                minHeight: minimumDimensionsByPanelType[type]![1],
            };
        }
    }, [expandablePanelSize, type]);

    React.useEffect(() => {
        panel.setWidth(expandablePanelSizeConfig[expandablePanelSize]);
        panel.synchronizeLayout();
    }, [expandablePanelSize, panel]);

    return { resizeOptions, sizeProps };
}
